import { ColumnDef, Row, SortingState, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import SkapaTable, { TableBody as SkapaTableBody, TableHeader as SkapaTableHeader } from '@ingka/table';
import { StyledTableDataDiv, StyledTableRow } from '../../../../shared/styles/components/table.styles';
import { useBulkEditState, useCreateBulkEditTableData } from './bulk-edit.state';

import { BulkEditSubItem } from '../../types/sub-item.types';
import { IndeterminateCheckbox } from '../../../../shared/components/IndeterminateCheckbox';
import { SortLabel } from '../table/SortLabel';
import { StyledSortWrapperDiv } from '../expanded-row/ExpandedTableHeader';
import { StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { range } from 'lodash';
import shallow from 'zustand/shallow';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { thousandSeparator } from '../../../../shared/utils/formatters.utils';
import { tokens } from '../../../../shared/styles/core/tokens';
import uniq from 'lodash/fp/uniq';

const StyledTable = styled(SkapaTable)`
  table {
    table-layout: auto;

    th:first-of-type {
      padding-left: ${tokens.space.lg};
    }
  }
`;

interface StyledInactiveWrapperDivProps {
   status: 'active' | 'inactive' | 'fullyInactive';
}

const StyledInactiveWrapperDiv = styled.div<StyledInactiveWrapperDivProps>`
  color: ${({ status }) => {
      switch (status) {
         case 'inactive':
            return theme.colors.bg.tertiary;
         case 'fullyInactive':
            return theme.colors.text.tertiary;
         default:
            return theme.colors.text.primary;
      }
   }};
  text-decoration: ${({ status }) => status === 'fullyInactive' ? 'line-through' : 'none'};
`;

export const BulkEditTable = () => {
   const [sorting, setSorting] = useState<SortingState>([]);

   useCreateBulkEditTableData();

   const [data, rowSelection, setRowSelection] = useBulkEditState(
      (state) => [state.tableData, state.rowSelection, state.setRowSelection],
      shallow
   );

   const [lastSelectedIndex, setLastSelectedIndex] = useState(0)

   const toggleRow = (row: Row<BulkEditSubItem>, value = !row.getIsSelected()) => {
      if (value) {
         setLastSelectedIndex(row.index)
      }

      row.toggleSelected(value)
   }

   const columns = useMemo<ColumnDef<BulkEditSubItem>[]>(
      () => {
         const baseColumns: ColumnDef<BulkEditSubItem>[] = [
            {
               id: 'select',
               accessorKey: '_',
               header: ({ table }) => (
                  <IndeterminateCheckbox
                     {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                     }}
                  />
               ),
               cell: ({ row }) => (
                  <div className="px-1" style={{ paddingLeft: tokens.space.lg }}>
                     <IndeterminateCheckbox
                        {...{
                           checked: row.getIsSelected(),
                           indeterminate: row.getIsSomeSelected(),
                           onChange: () => toggleRow(row),
                        }}
                     />
                  </div>
               ),
               meta: {
                  width: '4rem',
               },
               enableSorting: false,
            },
            {
               header: 'Part detail',
               accessorKey: 'partDetail',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Part',
               accessorKey: 'part',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Material grade',
               accessorKey: 'materialGrade',
               meta: {
                  width: '30rem',
               },
               enableSorting: true,
            },
            {
               header: 'MAM - Material grade ID',
               accessorKey: 'materialGradeId',
               meta: {
                  width: '15rem',
               },
               enableSorting: true,
            },
         ];

         if (data.some((item) => item.compositionShare !== undefined)) {
            baseColumns.push({
               header: 'Composition share (%)',
               accessorKey: 'compositionShare',
               meta: {
                  width: '10rem',
               },
            });
         }

         return [
            ...baseColumns,
            {
               header: 'Included in',
               accessorFn: (rowData) => rowData.subItems.length,
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Locked',
               accessorKey: 'locked',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Inactive',
               accessorKey: 'inactive',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: '3d model no.',
               accessorKey: 'threeDModelNo',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Quantity',
               accessorKey: 'quantity',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Volume (mm³)',
               accessorKey: 'volume',
               cell: (cell) => {
                  const value = parseFloat(cell.getValue() as string) || 0;
                  return thousandSeparator(Math.round(value));
               },
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Mass (g)',
               accessorKey: 'mass',
               cell: (cell) => {
                  const value = parseFloat(cell.getValue() as string) || 0;
                  return thousandSeparator(Math.round(value));
               },
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Density (kg/m³)',
               accessorKey: 'density',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Recycled content (%)',
               accessorKey: 'recycledContent',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Renewable content (%)',
               accessorKey: 'renewableContent',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
            {
               header: 'Comment',
               accessorKey: 'comment',
               meta: {
                  width: '10rem',
               },
               enableSorting: true,
            },
         ];
      },
      [data]
   );

   const instance = useReactTable({
      data,
      columns,
      state: {
         rowSelection: rowSelection,
         sorting: sorting,
      },
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   return (
      <StyledTable fullWidth={true}>
         <SkapaTableHeader sticky={true}>
            {instance.getHeaderGroups().map((headerGroup) => (
               <StyledTableRow key={headerGroup.id} noHover={true}>
                  {headerGroup.headers.map((header, index) => {
                     return (
                        <th
                           key={header.id}
                           colSpan={header.colSpan}
                           {...{
                              onClick: (e) => {
                                 if (index !== 0) header.column.getToggleSortingHandler()!(e);
                              },
                           }}
                           style={{
                              width: header.column.columnDef.meta?.width,
                              minWidth: header.column.columnDef.meta?.width,
                              maxWidth: header.column.columnDef.meta?.width,
                              cursor: 'pointer',
                           }}
                        >
                           <StyledTableDataDiv>
                              <StyledTooltip title={index !== 0 ? 'Toggle sort' : ''} placement="top">
                                 <StyledSortWrapperDiv>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {header.column.getCanSort() && (
                                       <SortLabel
                                          isSortedDesc={
                                             header.column.getIsSorted() === false ? undefined : header.column.getIsSorted() === 'desc'
                                          }
                                       />
                                    )}
                                 </StyledSortWrapperDiv>
                              </StyledTooltip>
                           </StyledTableDataDiv>
                        </th>
                     );
                  })}
               </StyledTableRow>
            ))}
         </SkapaTableHeader>

         <SkapaTableBody>
            {instance.getRowModel().rows.map((row) => {
               const allParts = row.original.subItems.length;
               const inactiveParts = row.original.inactive;
               const inactive = inactiveParts > 0;
               const fullyInactive = allParts === inactiveParts && allParts > 0;

               let status: 'active' | 'inactive' | 'fullyInactive' = 'active';
               if (fullyInactive) {
                  status = 'fullyInactive';
               } else if (inactive) {
                  status = 'inactive';
               }

               return (
                  <tr key={row.id} onClick={(ev) => {
                     ev.stopPropagation()

                     if (ev.ctrlKey) {
                        toggleRow(row)
                     } else if (ev.shiftKey) {
                        const index = row.index;
                        const rowModel = instance.getRowModel();
                        const rangeArgs: [number, number] = lastSelectedIndex < index ? [lastSelectedIndex, index + 1] : [index, lastSelectedIndex + 1]
                        const rowsRange = range(...rangeArgs)
                           .map((i) => rowModel.flatRows[i])
                           .filter((r) => !!r);

                        setLastSelectedIndex(index)

                        if (rowsRange.every((row) => row.getIsSelected())) {
                           rowsRange.forEach((row) => { row.toggleSelected(false) })
                        } else {
                           rowsRange.forEach((row) => { row.toggleSelected(true) })
                        }
                     } else {
                        instance.toggleAllRowsSelected(false)
                        toggleRow(row, true)
                     }
                  }} onMouseDown={(ev) => {
                     if (ev.shiftKey || ev.ctrlKey) {
                        ev.preventDefault()
                     }
                  }}>
                     {row.getVisibleCells().map((cell) => {
                        return (
                           <td key={cell.id}>
                              <StyledTooltip
                                 placement={'left'}
                                 title={
                                    cell.column.id === 'locked'
                                       ? uniq(row.original.subItems.filter((s) => s.locked).map((s) => s.parentId)).join(', ')
                                       : ''
                                 }
                              >
                                 <StyledInactiveWrapperDiv status={status}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                 </StyledInactiveWrapperDiv>
                              </StyledTooltip>
                           </td>
                        );
                     })}
                  </tr>
               );
            })}
         </SkapaTableBody>
      </StyledTable>
   );
};